import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserProfileStore } from '../store/user-profile.store';
import { UserService } from '../services/componentsServices/user.service';
import { UserResponse } from '../models/serviceModels/response/user.model';
import { ICvxClaimsPrincipal } from '@cvx-dev/cal-angular';
import { CalSessionService } from '../services/security/cal-session.service';

/**
 * Helper function to clean user session data from store and localStorage
 */
const cleanUserSession = (store: any): void => {
  store.CleanUserProfile();
  store.Authenticate(false);
  try {
    localStorage.removeItem('email');
    localStorage.removeItem('roleId');
    localStorage.removeItem('roleName');
  } catch {}
};

/**
 * Helper function to validate if a user is active and has a valid role
 */
const isValidActiveUser = (user: UserResponse | null): boolean => {
  return !!(user && user.idRole && user.idRole > 0 && user.isActiveUser === true);
};

/**
 * Helper function to get user email from claims principal
 */
const getPrincipalEmail = (principal: ICvxClaimsPrincipal | null): string => {
  if (!principal) return '';
  return principal.email || (principal as any)?.userName || '';
};

const applyPrincipal = (store: any, principal: ICvxClaimsPrincipal | null): string => {
  if (!principal) return '';

  store.SetUserProfile(principal as any);
  store.Authenticate(true);

  const email = getPrincipalEmail(principal);
  if (email) {
    store.SetUserEmail(email);
  }

  return email;
};

const resolveUserEmail = async (store: any, calSessionService: CalSessionService): Promise<string> => {
  let email = '';

  try { email = store.userProfile()?.email || ''; } catch {}
  if (email) return email;

  try { email = localStorage.getItem('email') || ''; } catch {}
  if (email) return email;

  const principal = await calSessionService.refreshPrincipal();
  return applyPrincipal(store, principal);
};

/**
 * Guard that checks if the user is registered and active in the database.
 * Redirects to /not-logged if user is not found, inactive, or has no valid role.
 * Redirects to /not-logged if email cannot be resolved.
 */
export const registeredUserGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const store = inject(UserProfileStore);
  const userService = inject(UserService);
  const router = inject(Router);
  const calSessionService = inject(CalSessionService);

  return from(resolveUserEmail(store, calSessionService)).pipe(
    switchMap((email) => {
      if (!email) {
        cleanUserSession(store);
        return of(router.parseUrl('/not-logged'));
      }

      const profile = store.userProfile();
      const roleId = profile?.roleId ?? null;
      const roleName = profile?.roleName ?? null;
      const hasValidRoleInStore = roleId != null && roleName && Number.isFinite(roleId) && roleId > 0;

      return userService.getFacilityUsersByEmail(email).pipe(
        map(user => {
          if (isValidActiveUser(user)) {
            if (!hasValidRoleInStore) {
              store.SetUserRoleName(user.roleName || '');
              store.SetUserRoleId(user.idRole);
            }
            return true;
          }

          cleanUserSession(store);
          return router.parseUrl('/not-logged');
        }),
        catchError(() => {
          cleanUserSession(store);
          return of(router.parseUrl('/not-logged'));
        })
      );
    }),
    catchError(() => {
      cleanUserSession(store);
      return of(router.parseUrl('/not-logged'));
    })
  );
};
